import React, { useState } from 'react';
import { X, Check, Clock, Utensils, Plus, Search } from 'lucide-react';

interface QuickMealLoggerProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (mealData: MealData) => void;
}

export interface MealData {
  name: string;
  mealType: 'breakfast' | 'lunch' | 'dinner' | 'snack';
  time: string;
  carbs: number;
  protein: number;
  fat: number;
  calories: number;
  notes?: string;
}

export const QuickMealLogger: React.FC<QuickMealLoggerProps> = ({ isOpen, onClose, onSave }) => {
  const [step, setStep] = useState<'type' | 'details'>('type');
  const initialMealData: MealData = {
    name: '',
    mealType: 'snack',
    time: new Date().toTimeString().substring(0, 5),
    carbs: 0,
    protein: 0,
    fat: 0,
    calories: 0,
    notes: ''
  };
  const [mealData, setMealData] = useState<MealData>(initialMealData);

  const [searchQuery, setSearchQuery] = useState('');
  
  const mealTypes = [
    { id: 'breakfast', label: 'Breakfast', icon: '🍳', time: '07:00' },
    { id: 'lunch', label: 'Lunch', icon: '🥗', time: '12:00' },
    { id: 'dinner', label: 'Dinner', icon: '🍽️', time: '18:00' },
    { id: 'snack', label: 'Snack', icon: '🍎', time: '15:00' }
  ];

  const quickFoodOptions = [
    { name: 'Apple', carbs: 25, protein: 0.5, fat: 0.3, calories: 95 },
    { name: 'Banana', carbs: 27, protein: 1.3, fat: 0.4, calories: 105 },
    { name: 'Greek Yogurt', carbs: 6, protein: 17, fat: 0.4, calories: 100 },
    { name: 'Whole Wheat Bread (2 slices)', carbs: 24, protein: 7, fat: 2, calories: 138 },
    { name: 'Chicken Breast (3oz)', carbs: 0, protein: 26, fat: 3, calories: 128 },
    { name: 'Avocado (1/2)', carbs: 6, protein: 2, fat: 15, calories: 160 },
    { name: 'Almonds (1oz)', carbs: 6, protein: 6, fat: 14, calories: 164 },
    { name: 'Oatmeal (1 cup)', carbs: 27, protein: 5, fat: 3, calories: 158 }
  ];

  const filteredOptions = searchQuery 
    ? quickFoodOptions.filter(food => 
        food.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : quickFoodOptions;

  const handleSelectMealType = (type: 'breakfast' | 'lunch' | 'dinner' | 'snack') => {
    const defaultTime = mealTypes.find(meal => meal.id === type)?.time || new Date().toTimeString().substring(0, 5);
    setMealData(prev => ({ ...prev, mealType: type, time: defaultTime }));
    setStep('details');
  };

  const handleSelectFood = (food: typeof quickFoodOptions[0]) => {
    setMealData(prev => ({
      ...prev,
      name: food.name,
      carbs: food.carbs,
      protein: food.protein,
      fat: food.fat,
      calories: food.calories
    }));
  };

  const handleSave = () => {
    onSave(mealData);
    resetForm();
    onClose();
  };

  const resetForm = () => {
    setMealData(initialMealData);
    setStep('type');
    setSearchQuery('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 md:p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[95vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center transform rotate-12">
                <Utensils className="h-5 w-5 text-white" />
              </div> 
              <h2 className="text-xl font-bold bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent">
                {step === 'type' ? 'Quick Log Meal' : `Log ${mealData.mealType.charAt(0).toUpperCase() + mealData.mealType.slice(1)}`}
              </h2>
            </div>
            <button 
              onClick={handleClose}
              className="text-slate-400 hover:text-slate-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        <div className="p-6">
          {step === 'type' ? (
            <div className="space-y-6">
              <p className="text-sm md:text-base text-slate-600">What are you logging?</p>
              
              <div className="grid grid-cols-2 gap-4">
                {mealTypes.map((meal) => (
                  <button
                    key={meal.id}
                    onClick={() => handleSelectMealType(meal.id as any)}
                    className="p-4 border-2 border-slate-200 rounded-xl hover:border-orange-500 hover:shadow-md transition-all flex flex-col items-center space-y-2"
                  >
                    <div className="text-3xl">{meal.icon}</div>
                    <span className="font-medium text-slate-800">{meal.label}</span>
                    <div className="flex items-center space-x-1 text-xs text-slate-500">
                      <Clock className="h-3 w-3" />
                      <span>{meal.time}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-1.5 md:p-2 bg-orange-100 rounded-lg">
                  <Clock className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <label htmlFor="meal-time" className="block text-sm font-medium text-slate-700">Time</label>
                  <input
                    id="meal-time"
                    type="time"
                    value={mealData.time}
                    onChange={(e) => setMealData(prev => ({ ...prev, time: e.target.value }))}
                    className="mt-1 p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1 md:mb-2">Quick Select Food</label>
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search common foods or enter custom..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-48 overflow-y-auto mb-4">
                  {filteredOptions.map((food, index) => (
                    <button
                      key={index}
                      onClick={() => handleSelectFood(food)}
                      className={`p-3 text-left border rounded-lg transition-colors ${
                        mealData.name === food.name 
                          ? 'border-orange-500 bg-orange-50' 
                          : 'border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      <div className="font-medium text-slate-800">{food.name}</div>
                      <div className="text-xs text-slate-500">{food.calories} cal • {food.carbs}g carbs</div>
                    </button>
                  ))}
                  {filteredOptions.length === 0 && (
                    <button
                      onClick={() => setMealData(prev => ({ ...prev, name: searchQuery }))}
                      className="col-span-2 p-3 text-left border border-dashed border-slate-300 rounded-lg hover:border-orange-500 transition-colors"
                    >
                      <div className="flex items-center space-x-2">
                        <Plus className="h-4 w-4 text-orange-500" />
                        <span className="font-medium text-slate-800">Add "{searchQuery}"</span>
                      </div> 
                    </button>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1 md:mb-2">Nutrition Information</label>
                <div className="grid grid-cols-2 gap-3 md:gap-4">
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Carbs (g)</label>
                    <input
                      type="number"
                      value={mealData.carbs}
                      onChange={(e) => setMealData(prev => ({ ...prev, carbs: Number(e.target.value) }))}
                      className="w-full p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Protein (g)</label>
                    <input
                      type="number"
                      value={mealData.protein}
                      onChange={(e) => setMealData(prev => ({ ...prev, protein: Number(e.target.value) }))}
                      className="w-full p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Fat (g)</label>
                    <input
                      type="number"
                      value={mealData.fat}
                      onChange={(e) => setMealData(prev => ({ ...prev, fat: Number(e.target.value) }))}
                      className="w-full p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Calories</label>
                    <input
                      type="number"
                      value={mealData.calories}
                      onChange={(e) => setMealData(prev => ({ ...prev, calories: Number(e.target.value) }))}
                      className="w-full p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1 md:mb-2">Notes (optional)</label>
                <textarea
                  value={mealData.notes}
                  onChange={(e) => setMealData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Add any additional details..."
                  className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  rows={2}
                />
              </div>

              <div className="pt-4 flex items-center space-x-3">
                <button
                  onClick={() => setStep('type')}
                  className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handleSave}
                  disabled={!mealData.name}
                  className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 disabled:opacity-50 text-white px-4 py-2 rounded-lg flex items-center justify-center space-x-2 transition-all transform hover:scale-105"
                >
                  <Check className="h-4 w-4" />
                  <span>Save {mealData.mealType}</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
